if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer') && this.dataset.directCheckout !== 'true') this.submitButton.setAttribute('aria-haspopup', 'dialog');
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted || !this.checkoutNavigating) return;
          window.HMCheckoutFeedback?.set(this, false);
          this.checkoutNavigating = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('aria-busy');
          this.cart?.markCrossTabDirty?.();
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        });
        // Noom (Kamil 14.09, old product pages): Elixir's product-form.js kept the configured label of the buy button
        const label = this.submitButton.querySelector('.button-text');
        const persistent = (this.submitButton.dataset.persistentText || '').trim();
        if (label && persistent && this.submitButton.dataset.textOverride === 'true') {
          const showPrice = this.submitButton.dataset.showPrice === 'true';
          const keep = () => {
            if (this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-busy') === 'true') return;
            const current = label.textContent.trim();
            if (showPrice ? !current.startsWith(persistent) : current !== persistent) label.textContent = persistent;
          };
          keep();
          new MutationObserver(keep).observe(label, { characterData: true, childList: true, subtree: true });
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.checkoutNavigating || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const directCheckout = this.dataset.directCheckout === 'true';
        const exactQty = directCheckout && this.dataset.exactQty === 'true' && typeof window.HMExactQty?.submitProductForm === 'function';
        if (exactQty) {
          // Noom (naprawa koszyka, etap 2): each exact click reads the cart and sets the quantity, so no stored recovery is ever replayed.
          this.clearCheckoutRecovery();
        }
        const recovery = directCheckout ? this.getCheckoutRecovery() : null;
        const deadlineAt = directCheckout ? Date.now() + ProductForm.ADD_TIMEOUT_MS : null;

        // LP bonus shares the paid operation; a failed bonus never retries the paid add.
        const bonus = window.HMLpBonus;
        const bonusUI = window.HMLpBonusUI;
        const formData = new FormData(this.form);
        if (this.form.hmRetryData) {
          for (const key of new Set(Array.from(formData.keys()))) formData.delete(key);
          this.form.hmRetryData.forEach(([key, value]) => formData.append(key, value));
          delete this.form.hmRetryData;
        }
        if (recovery) {
          for (const key of new Set(Array.from(formData.keys()))) formData.delete(key);
          recovery.fields.forEach(([key, value]) => formData.append(key, value));
        }
        // A current receipt can reveal a Knee/bonus added in another tab.
        // Direct checkout validates it locally; ordinary Arm adds still need only one request.
        const useBonus = bonus && (bonus.isEligibleForm(formData) || bonus.relevant || (directCheckout && bonus.enabled));
        const bonusToken = useBonus ? bonus.begin() : null;
        if (bonus && bonus.active && !bonusToken) return;
        if (bonusToken && bonusUI && !directCheckout) bonusUI.render();

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        const hasFeedback = directCheckout && window.HMCheckoutFeedback && this.dataset.checkoutLoading;
        if (!hasFeedback) this.submitButton.classList.add('loading');
        if (hasFeedback) window.HMCheckoutFeedback.set(this, true);
        if (directCheckout) this.submitButton.setAttribute('aria-busy', 'true');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner && !hasFeedback) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const sections = directCheckout ? ['hm-checkout-state'] : this.cart?.getSectionsToRender().map(section => section.id) || [];
        if (this.cart || directCheckout) {
          formData.append(
            'sections',
            sections
          );
          formData.append('sections_url', window.location.pathname);
          this.cart?.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const bundledAdd = !recovery && bonusToken && (directCheckout || this.cart?.getSectionsToRender().some(section => section.id === 'hm-cart-drawer'))
          ? bonusUI?.prepareAdd(formData) : null;
        if (bundledAdd) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({ ...bundledAdd,
            sections,
            sections_url: window.location.pathname + window.location.search });
        }
        if (exactQty && window.HMExactQty.supports(formData)) {
          // Noom (naprawa koszyka, etap 2): "Kup teraz" sets the exact quantity (assets/hm-exact-qty.js) instead of adding to the cart.
          return window.HMExactQty.submitProductForm(this, evt, { formData, config, deadlineAt, bonus, bonusUI, bonusToken, bundledAdd });
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = recovery ? null : this.createCartLinesUpdateEvent(variantId, quantity);
        let analyticsResolved = false;

        // Noom (T02, 13.09): the store may answer without JSON (429 + Cloudflare HTML, 5xx, an empty body), never answer
        const controller = !directCheckout && typeof AbortController === 'function' ? new AbortController() : null;
        if (controller) config.signal = controller.signal;
        const abortTimer = controller ? window.setTimeout(() => controller.abort(), ProductForm.ADD_TIMEOUT_MS) : null;
        this.addConfirmed = false;
        if (directCheckout && !recovery) this.rememberCheckout(formData, false);
        const request = directCheckout
          ? this.readDirectCart(recovery ? `${window.Shopify?.routes?.root || '/'}cart.js` : routes.cart_add_url,
            recovery ? { headers: { Accept: 'application/json' } } : config, deadlineAt)
          : fetch(`${routes.cart_add_url}`, config).then(response => this.readCartResponse(response));

        return request
          .then(async (parsed) => {
            if (!parsed.isJson) {
              throw Object.assign(new Error(`cart/add answered ${parsed.status} without JSON`), { unconfirmed: true, status: parsed.status });
            }
            let response = parsed.data;
            if (directCheckout && (!parsed.ok || response.status) &&
                (recovery || !(parsed.status === 422 || (parsed.ok && Number(response.status) === 422)))) {
              throw Object.assign(new Error('Checkout write is unconfirmed'), { unconfirmed: true, status: parsed.status });
            }
            // A multi-line refusal may be partial: read before another add, never replay it automatically.
            if (bundledAdd && (!parsed.ok || response.status)) {
              throw Object.assign(new Error('Bundled add was not fully confirmed'), { unconfirmed: true, status: parsed.status });
            }
            if (!parsed.ok || response.status) {
              if (directCheckout) this.clearCheckoutRecovery();
              bonusUI?.cancelCheckout();
              // Shopify's own 429 is a refusal with an English description: say it in the store's words
              const description = Number(response.status) === 429 ? this.errorText('ratelimit') : response.description;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                kind: 'refused',
                directCheckout,
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(description);
              this.dispatchCartErrorEvent(description || response.message, 'INVALID');
              this.rejectQuietly(linesUpdateDeferred, new Error(description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (directCheckout) {
              if (recovery && (recovery.requiresReview || !this.validCheckoutCart(response) ||
                  !this.confirmsCheckoutLine(formData, response, bonus, recovery.minimum))) {
                this.clearCheckoutRecovery();
                throw Object.assign(new Error('Previous purchase was not found; review the cart before another attempt'), { recoveryAbsent: true });
              }
              const addedLines = Array.isArray(response.items) ? response.items : [response];
              if (!addedLines.some(line => String(line.variant_id || line.id) === String(variantId) && Number(line.quantity) >= quantity)) {
                throw Object.assign(new Error('Paid product was not confirmed before checkout'), { unconfirmed: true });
              }
            }

            this.addConfirmed = directCheckout || !bundledAdd;
            if (directCheckout) {
              this.rememberCheckout(formData, true, response);
            }
            if (bonusToken && this.cart) {
              window.clearTimeout(this.cart.addingTimer);
            }
            response = this.cart?.cartFromResponse?.(response) || response;
            if (directCheckout && !this.validCheckoutCart(response)) {
              response = (await this.readDirectCart(`${window.Shopify?.routes?.root || '/'}cart.js`,
                { headers: { Accept: 'application/json' } }, deadlineAt)).data;
              if (!this.validCheckoutCart(response)) throw new Error('Checkout cart is not confirmed');
            }
            if (directCheckout && bonus?.enabled && !bonusUI) throw new Error('Bonus verification is unavailable');
            if (directCheckout && !analyticsResolved) {
              this.resolveCartLinesUpdate(linesUpdateDeferred, response);
              analyticsResolved = true;
            }
            if (bonusToken && bonusUI) {
              response = await bonusUI.settle(response, directCheckout ? [] : sections,
                bundledAdd ? formData : undefined, directCheckout ? { strict: true, deadlineAt } : undefined);
            }
            if (bonusToken && this.cart) this.cart.pendingAdd = null;
            if (directCheckout) {
              // The paid add and any gift are confirmed before leaving the PDP.
              this.addConfirmed = true;
              this.error = false;
              if (!this.validCheckoutCart(response)) throw new Error('Checkout cart is not confirmed');
              if (!this.confirmsCheckoutLine(formData, response, bonus)) throw new Error('Paid line missing after bonus verification');
              this.clearCheckoutRecovery();
              if (!analyticsResolved) this.resolveCartLinesUpdate(linesUpdateDeferred, response);
              if (this.cart) {
                this.cart.pendingAdd = null;
                this.cart.setAdding?.(false);
                this.cart.unconfirmed = null;
                this.cart.setNeedsSync?.(false);
                this.cart.crossTabDirty = true;
              }
              this.checkoutNavigating = true;
              const root = window.Shopify?.routes?.root || '/';
              window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
              return;
            }
            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred, response);
              window.location = window.routes.cart_url;
              return;
            }

            this.addConfirmed = true;
            this.resolveCartLinesUpdate(linesUpdateDeferred, response);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
                drawerRenderedBySource: this.cart.tagName === 'CART-DRAWER' && this.cart.sectionsComplete(response.sections),
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            this.checkoutNavigating = false;
            if (bonusToken && bonusUI) bonusUI.markDirty();
            console.error(e);
            const reason = e && e.name === 'AbortError' ? 'timeout' : e && e.unconfirmed ? (e.status === 429 ? 'ratelimit' : 'html') : 'network';
            const message = this.errorText(reason === 'ratelimit' ? 'ratelimit' : reason === 'network' ? 'network' : 'unconfirmed');
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              kind: 'unconfirmed',
              directCheckout,
              reason,
              status: e && e.status,
              productVariantId: variantId,
              quantity,
              message,
            });
            if (e.recoveryAbsent && this.cart) {
              this.cart.unconfirmed = null;
              this.cart.pendingAdd = null;
              this.cart.setNeedsSync?.(false);
            }
            this.handleErrorMessage(message);
            this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
            this.rejectQuietly(linesUpdateDeferred, e);
          })
          .finally(() => {
            if (bonusToken) bonus.end(bonusToken);
            if (bonusUI && !directCheckout) bonusUI.render();
            // A confirmed add can change discounts and line keys in every open tab.
            // Signal after bonus settlement, also when only the paid add succeeded.
            if (this.addConfirmed) {
              try { localStorage.setItem('noom-cart-revision', `${Date.now()}:${Math.random()}`); } catch (_) { /* private mode */ }
            }
            if (abortTimer) window.clearTimeout(abortTimer);
            if (!this.checkoutNavigating) {
              window.HMCheckoutFeedback?.set(this, false);
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-busy');
            }
            // only a confirmed add may drop the empty state: an error must leave the drawer as it was (empty or not)
            if (this.addConfirmed && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.checkoutNavigating) this.submitButton.removeAttribute('aria-disabled');
            const spinnerEnd = this.querySelector('.loading__spinner');
            if (spinnerEnd && !this.checkoutNavigating) spinnerEnd.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getCheckoutRecovery() {
        try {
          const saved = this.checkoutRecovery || JSON.parse(window.sessionStorage.getItem('hm-checkout-pending') || 'null');
          if (!saved || saved.route !== window.location.pathname ||
              saved.variant !== String(new FormData(this.form).get('id')) || !Array.isArray(saved.fields)) return null;
          if (!saved.fields.every(pair => Array.isArray(pair) && pair.length === 2 && pair.every(value => typeof value === 'string'))) return null;
          if (!Number.isSafeInteger(saved.minimum) || saved.minimum < 1) return null;
          const current = new FormData(this.form);
          const intent = entries => JSON.stringify(Array.from(entries).filter(([key]) =>
            ['id', 'quantity', 'selling_plan'].includes(key) || /^properties\[/.test(key)).sort(([a], [b]) => a.localeCompare(b)));
          saved.requiresReview = Date.now() - saved.createdAt > 30 * 60 * 1000 || intent(saved.fields) !== intent(current.entries());
          this.checkoutRecovery = saved;
          return saved;
        } catch (_) { return null; }
      }

      rememberCheckout(form, confirmed, response) {
        const bonus = window.HMLpBonus;
        const previous = this.checkoutRecovery;
        const current = bonus?.currentCart;
        const identity = bonus?.identity?.(form);
        const before = current?.items?.filter(item => identity && bonus.identityOfItem(item) === identity)
          .reduce((sum, item) => sum + item.quantity, 0) ?? this.cart?.pendingAdd?.qtyBefore ?? 0;
        const matching = (response?.items || (response ? [response] : [])).filter(item =>
          String(item.variant_id || item.id) === String(form.get('id')) &&
          (!identity || bonus.identityOfItem(item) === identity));
        const expected = previous?.minimum || before + (Number(form.get('quantity')) || 1);
        const minimum = confirmed && matching.length
          ? Math.max(expected, matching.reduce((sum, item) => sum + Number(item.quantity || 0), 0)) : expected;
        const record = { route: window.location.pathname, variant: String(form.get('id')), minimum,
          fields: Array.from(form.entries()).filter(([key, value]) => typeof value === 'string' && !['sections', 'sections_url'].includes(key)),
          confirmed, createdAt: Date.now() };
        this.checkoutRecovery = record;
        try { window.sessionStorage.setItem('hm-checkout-pending', JSON.stringify(record)); } catch (_) { /* Storage can be unavailable. */ }
      }

      clearCheckoutRecovery() {
        this.checkoutRecovery = null;
        try { window.sessionStorage.removeItem('hm-checkout-pending'); } catch (_) { /* private mode */ }
      }

      validCheckoutCart(cart) {
        const totals = ['item_count', 'items_subtotal_price', 'original_total_price', 'total_price', 'total_discount', 'total_weight'];
        return !!cart && /^[A-Z]{3}$/.test(cart.currency || '') &&
          totals.every(key => Number.isFinite(cart[key]) && cart[key] >= 0) && Array.isArray(cart.items) &&
          cart.items.every(item => item && typeof item.key === 'string' && item.key.trim() &&
            Number.isSafeInteger(item.variant_id) && item.variant_id > 0 &&
            Number.isSafeInteger(item.quantity) && item.quantity > 0);
      }

      confirmsCheckoutLine(form, cart, bonus, minimum) {
        if (minimum == null && bonus?.enabled && bonus.confirmsAdd) return bonus.confirmsAdd(form, cart);
        const properties = {};
        for (const [key, value] of form.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match && String(value) !== '') properties[match[1]] = String(value);
        }
        const normalize = value => JSON.stringify(Object.entries(value || {}).filter(([, v]) => v != null && String(v) !== '')
          .map(([k, v]) => [k, String(v)]).sort(([a], [b]) => a.localeCompare(b)));
        return cart.items.filter(item => String(item.variant_id) === String(form.get('id')) &&
          String(item.selling_plan_allocation?.selling_plan?.id || '') === String(form.get('selling_plan') || '') &&
          normalize(item.properties) === normalize(properties)).reduce((sum, item) => sum + item.quantity, 0) >= (minimum == null ? Number(form.get('quantity')) || 1 : minimum);
      }

      // One deadline for add/read and the subsequent bonus settlement, including body consumption.
      async readDirectCart(url, options, deadlineAt) {
        const remaining = deadlineAt - Date.now();
        if (remaining <= 0) throw new Error('Checkout confirmation timed out');
        const controller = new AbortController();
        let timer;
        const timeout = new Promise((_, reject) => {
          timer = window.setTimeout(() => {
            controller.abort();
            reject(Object.assign(new Error('Checkout confirmation timed out'), { name: 'AbortError', unconfirmed: true }));
          }, remaining);
        });
        try {
          const result = await Promise.race([
            fetch(url, { ...options, signal: controller.signal }).then(response => this.readCartResponse(response)), timeout,
          ]);
          if (!result.ok || !result.isJson) {
            // Preserve an explicit Shopify refusal for the normal add error path.
            if (result.isJson) return result;
            throw Object.assign(new Error('Unconfirmed checkout response'), { unconfirmed: true, status: result.status });
          }
          return result;
        } finally { window.clearTimeout(timer); }
      }

      // Noom (T02, 13.09): no answer within this time = unconfirmed add (the request is aborted, never re-sent)
      static get ADD_TIMEOUT_MS() {
        return 12000;
      }

      // reject the analytics deferred (cart:lines-update) without an "unhandled rejection" in the console when no one awaits it
      rejectQuietly(deferred, error) {
        if (!deferred) return;
        // tagged so a page-side listener can tell this analytics rejection from a real fault (Shopify's consumer of the
        // cart:lines-update promise reports it as an unhandled rejection; Dawn's own 422 path does the same)
        if (error && typeof error === 'object') error.hmDeferred = 'cart-lines-update';
        if (deferred.promise && typeof deferred.promise.catch === 'function') deferred.promise.catch(() => {});
        deferred.reject(error);
      }

      // the body as JSON when it is JSON; `isJson: false` for an HTML challenge page, an empty body or a broken document
      readCartResponse(response) {
        return response.text().then((text) => {
          let data = null;
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = null;
          }
          return { status: response.status, ok: response.ok, data, isJson: !!data && typeof data === 'object' };
        });
      }

      // wording lives on the drawer's error card (snippets/hm-cart-drawer.liquid); Dawn's generic line is the fallback
      errorText(kind) {
        if (this.cart && typeof this.cart.errorText === 'function') return this.cart.errorText(kind);
        return (window.cartStrings && window.cartStrings.error) || 'Could not add this item. Please try again.';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, confirmedCart) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = confirmedCart?.currency && Array.isArray(confirmedCart.items)
          ? Promise.resolve(confirmedCart) : typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
